import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { FileText, Clock, CheckCircle, BarChart2, PieChart as PieChartIcon, Inbox, ArrowRight, FileSearch, BookOpen, ClipboardCheck, RefreshCw, Banknote, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../../auth/hooks/auth';
import { dashboardService, DashboardStats, SubmissionChartData, DeskDashboardStats } from '../services/adminAuth';
import { accessorService, GetReviewerAssignmentPayload, ReviewerAssignmentItem } from '../services/adminAuth';
import { categoryAdminService } from '../services/adminAuth';
import type { Category } from '../services/common/types';
import { Modal } from '../components/adminModals';

interface StatCard {
    title: string;
    value: string;
    icon: React.ReactNode;
    iconBg: string;
    subItems?: { label: string; value: string | number }[];
}

function generateDistinctColors(count: number, startHue = 0): string[] {
    const goldenRatio = 0.618033988749895;

    return Array.from({ length: count }, (_, i) => {
        const hue = ((startHue + i * goldenRatio * 360) % 360).toFixed(0);
        return `hsl(${hue}, 60%, 48%)`;
    });
}

interface DeskItem {
    key: keyof DeskDashboardStats;
    label: string;
    route: string;
    icon: React.ReactNode;
    iconBg: string;
}

const DESK_ITEMS: DeskItem[] = [
    { key: 'submissionByDeskCount', label: 'Submissions on your desk', route: '/admin/submissions-desk', icon: <FileText className="w-5 h-5 text-blue-600" />, iconBg: 'bg-blue-50' },
    { key: 'pendingAssignedPrimafacieCount', label: 'Prima facie assignments pending', route: '/admin/prima-facie-assignments', icon: <FileSearch className="w-5 h-5 text-yellow-600" />, iconBg: 'bg-yellow-50' },
    { key: 'reviewedManuscriptByDeskCount', label: 'Reviewed manuscripts on your desk', route: '/admin/reviewed-manuscripts-desk', icon: <ClipboardCheck className="w-5 h-5 text-purple-600" />, iconBg: 'bg-purple-50' },
    { key: 'pendingAssignedManuscriptEvaluationCount', label: 'Reviewer evaluation assignments pending', route: '/admin/reviewer-evaluation-assignments', icon: <ClipboardCheck className="w-5 h-5 text-indigo-600" />, iconBg: 'bg-indigo-50' },
    { key: 'pendingReReviewManuscriptRequestCount', label: 'Re-review evaluation assignments  pending', route: '/admin/reviewed-manuscripts-for-rereview', icon: <RefreshCw className="w-5 h-5 text-orange-600" />, iconBg: 'bg-orange-50' },
    { key: 'journalByDeskCount', label: 'Journals on your desk', route: '/admin/journals-desk', icon: <BookOpen className="w-5 h-5 text-teal-600" />, iconBg: 'bg-teal-50' },
    { key: 'pendingAssignedJournalAssessmentCount', label: 'Journal assessments pending', route: '/admin/journal-assessment-assignments', icon: <BookOpen className="w-5 h-5 text-cyan-600" />, iconBg: 'bg-cyan-50' },
    { key: 'revisedCopyByDeskCount', label: 'Revised copies on your desk', route: '/admin/revised-submissions', icon: <RefreshCw className="w-5 h-5 text-pink-600" />, iconBg: 'bg-pink-50' },
    { key: 'unPaidReviewerPaymentCount', label: 'Pending reviewer payments', route: '/admin/reviewer-payments', icon: <Banknote className="w-5 h-5 text-green-600" />, iconBg: 'bg-green-50' },
];

function formatReviewDate(dateString: string | null): string {
    if (!dateString) return 'N/A';
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return 'N/A';
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });
}

// Turn PascalCase / camelCase API statuses (e.g. "UnderReview") into "Under Review".
function humanizeStatus(status: string): string {
    if (!status) return '';
    return status.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' ').trim();
}

function ReviewStatusBadge({ status }: { status: string }) {
    const name = (status || '').toLowerCase();
    let cls = 'bg-blue-100 text-blue-700';
    let label = humanizeStatus(status) || 'Pending';
    if (name.includes('approv') || name.includes('complet') || name.includes('pass') || name.includes('accept')) {
        cls = 'bg-green-100 text-green-700';
        label = 'Reviewed';
    } else if (name.includes('review') || name.includes('pending') || name.includes('progress')) {
        cls = 'bg-yellow-100 text-yellow-700';
    } else if (name.includes('return') || name.includes('reject') || name.includes('declin') || name.includes('fail')) {
        cls = 'bg-red-100 text-red-700';
    }
    return <span className={`px-3 py-1 rounded-full text-xs font-medium ${cls}`}>{label}</span>;
}

function StatCardSkeleton() {
    return (
        <div className="bg-white rounded-lg border border-gray-200 p-6 animate-pulse">
            <div className="flex items-start justify-between mb-4">
                <div className="h-4 bg-gray-200 rounded w-32" />
                <div className="w-10 h-10 rounded-lg bg-gray-200" />
            </div>
            <div className="h-8 bg-gray-200 rounded w-24 mb-1" />
        </div>
    );
}

function ChartSkeleton({ height = 300 }: { height?: number }) {
    return (
        <div className="animate-pulse flex items-end gap-3 justify-center" style={{ height }}>
            {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="bg-gray-100 rounded w-8 flex-shrink-0" style={{ height: `${20 + (i % 3) * 25}%` }} />
            ))}
        </div>
    );
}

function DeskSkeleton() {
    return (
        <div className="space-y-3 animate-pulse">
            {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-lg border border-gray-100">
                    <div className="w-10 h-10 rounded-lg bg-gray-200 flex-shrink-0" />
                    <div className="flex-1">
                        <div className="h-4 bg-gray-200 rounded w-44 mb-2" />
                        <div className="h-3 bg-gray-100 rounded w-28" />
                    </div>
                    <div className="w-8 h-6 bg-gray-200 rounded" />
                </div>
            ))}
        </div>
    );
}

function CycleBarTooltip({ active, payload, label }: any) {
    if (!active || !payload?.length) return null;
    const total = payload.find((p: any) => p.dataKey === 'totalSubmissions')?.value ?? 0;
    const approved = payload.find((p: any) => p.dataKey === 'approvedCount')?.value ?? 0;
    const rejected = payload.find((p: any) => p.dataKey === 'rejectedCount')?.value ?? 0;
    return (
        <div className="bg-white border border-gray-200 rounded-lg px-4 py-3 shadow-md text-sm">
            <p className="font-semibold text-gray-900 mb-1">{label}</p>
            <p className="text-gray-700">Total: <span className="font-medium text-gray-900">{total}</span></p>
            <p className="text-green-600">Approved: <span className="font-medium">{approved}</span></p>
            <p className="text-red-500">Rejected: <span className="font-medium">{rejected}</span></p>
        </div>
    );
}

function PieTooltip({ active, payload }: any) {
    if (!active || !payload?.length) return null;
    const { name, value, payload: inner } = payload[0];
    return (
        <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 shadow-md text-sm">
            <p className="font-semibold text-gray-900">{name}</p>
            <p className="text-gray-500">Submissions: <span className="font-medium text-gray-800">{value}</span></p>
            <p className="text-gray-400">{(inner.pct ?? 0).toFixed(1)}% of total</p>
        </div>
    );
}

function PieLegend({ items }: { items: { name: string; color: string; value: number; pct: number }[] }) {
    return (
        <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 mt-4">
            {items.map((item) => (
                <div key={item.name} className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
                    <span className="text-sm text-gray-600">
                        {item.name}
                        <span className="ml-1 text-gray-400">({item.pct.toFixed(1)}%)</span>
                    </span>
                </div>
            ))}
        </div>
    );
}

function DashboardOverview() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const isAccessor = user?.role === 'accessor';
    const normalizedDisplayRole = (user?.displayRole ?? '').toLowerCase().replace(/[\s_-]/g, '');
    const isBookConvener = normalizedDisplayRole === 'bookconvener';
    const isJournalConvener = user?.role === 'journalconvener' || normalizedDisplayRole === 'journalconvener';

    // Thematic-area users (leader or member) see a bar chart scoped to their own thematic area.
    const isThematicUser = user?.role === 'thematicarealeader' || user?.role === 'tagthematicareamember';

    // Chart visibility:
    //  - Book convener: hide the Application Type pie chart.
    //  - Journal convener: hide both pie charts (Thematic Area + Application Type).
    //  - Thematic-area leader/member: hide the Application Type pie chart.
    const showThematicChart = !isJournalConvener;
    const showAppTypeChart = !isJournalConvener && !isBookConvener && !isThematicUser;

    const [stats, setStats] = useState<DashboardStats | null>(null);
    const [statsLoading, setStatsLoading] = useState(true);

    const [chartData, setChartData] = useState<SubmissionChartData | null>(null);
    const [chartsLoading, setChartsLoading] = useState(true);

    // For thematic-area users: the name(s) of their own thematic area(s), used to scope the bar chart.
    const [thematicCategoryNames, setThematicCategoryNames] = useState<string[]>([]);

    const [deskStats, setDeskStats] = useState<DeskDashboardStats | null>(null);
    const [deskLoading, setDeskLoading] = useState(true);

    // Accessor-specific stats
    const [accessorTotal, setAccessorTotal] = useState(0);
    const [accessorPending, setAccessorPending] = useState(0);
    const [accessorCompleted, setAccessorCompleted] = useState(0);
    const [accessorReturned, setAccessorReturned] = useState(0);

    const [recentReviews, setRecentReviews] = useState<ReviewerAssignmentItem[]>([]);
    const [reviewsLoading, setReviewsLoading] = useState(true);
    const [conflictTarget, setConflictTarget] = useState<ReviewerAssignmentItem | null>(null);

    useEffect(() => {
        const fetchStats = async () => {
            setStatsLoading(true);
            try {
                if (isAccessor) {
                    const res = await dashboardService.getReviewerDashboardStatistics();
                    if (res.isSuccess && res.data) {
                        setAccessorTotal(res.data.totalAssignment);
                        setAccessorPending(res.data.totalPending);
                        setAccessorCompleted(res.data.totalReviewed);
                        setAccessorReturned(res.data.totalReturnedManuscript);
                    }
                } else {
                    const res = await dashboardService.getAdminDashboardStatistics();
                    if (res.isSuccess) setStats(res.data);
                }
            } catch (err) {
                } finally {
                setStatsLoading(false);
            }
        };

        const fetchCharts = async () => {
            setChartsLoading(true);
            try {
                const res = await dashboardService.getSubmissionCharts();
                if (res.isSuccess) setChartData(res.data);
            } catch (err) {
                } finally {
                setChartsLoading(false);
            }
        };

        // Thematic-area users: resolve their own thematic area(s) so the bar chart can be scoped to them.
        const fetchThematicCategories = async () => {
            try {
                const res = await categoryAdminService.getCategoriesForCategoryAdmin();
                if (res.isSuccess && res.data) setThematicCategoryNames(res.data.map((c: Category) => c.name));
            } catch (err) {
                // Non-fatal: bar chart falls back to per-cycle data if we can't resolve the category.
            }
        };

        const fetchDeskStats = async () => {
            setDeskLoading(true);
            try {
                const res = await dashboardService.getDeskDashboardStatistics();
                if (res.isSuccess) setDeskStats(res.data);
            } catch (err) {
                } finally {
                setDeskLoading(false);
            }
        };

        const fetchRecentReviews = async () => {
            setReviewsLoading(true);
            try {
                const payload: GetReviewerAssignmentPayload = {
                    pageNumber: 1,
                    pageSize: 5,
                    sortBy: '',
                    sortDescending: true,
                };
                const res = await accessorService.getReviewerAssignment(payload);
                if (res.isSuccess && res.data) setRecentReviews(res.data.data);
            } catch (err) {
                } finally {
                setReviewsLoading(false);
            }
        };

        fetchStats();
        fetchDeskStats();
        if (isAccessor) {
            fetchRecentReviews();
        } else {
            fetchCharts();
            if (isThematicUser) fetchThematicCategories();
        }
    }, [isAccessor, isThematicUser]);

    const submissionsCard: StatCard | null = stats
        ? (isBookConvener || isThematicUser
            ? {
                title: 'Book Submissions',
                value: stats.totalBookSubmissions.toLocaleString(),
                icon: <FileText className="w-5 h-5 text-blue-600" />,
                iconBg: 'bg-blue-50',
            }
            : isJournalConvener
                ? {
                    title: 'Journal Submissions',
                    value: stats.totalJournalSubmissions.toLocaleString(),
                    icon: <FileText className="w-5 h-5 text-blue-600" />,
                    iconBg: 'bg-blue-50',
                }
                : {
                    title: 'Submissions',
                    value: (stats.totalJournalSubmissions + stats.totalBookSubmissions).toLocaleString(),
                    icon: <FileText className="w-5 h-5 text-blue-600" />,
                    iconBg: 'bg-blue-50',
                    subItems: [
                        { label: 'Journals', value: stats.totalJournalSubmissions.toLocaleString() },
                        { label: 'Books', value: stats.totalBookSubmissions.toLocaleString() },
                    ],
                })
        : null;

    const primaFacieCard: StatCard | null = stats
        ? {
            title: 'Prima Facie',
            value: (stats.primaFacieRecommended + stats.primaFacieNotRecommended + stats.pendingPrimaFacie).toLocaleString(),
            icon: <Clock className="w-5 h-5 text-yellow-600" />,
            iconBg: 'bg-yellow-50',
            subItems: [
                { label: 'Recommended', value: stats.primaFacieRecommended.toLocaleString() },
                { label: 'Not Recommended', value: stats.primaFacieNotRecommended.toLocaleString() },
                { label: 'Pending', value: stats.pendingPrimaFacie.toLocaleString() },
            ],
        }
        : null;

    const publicationCard: StatCard | null = stats
        ? {
            title: 'Reviewer Assessment Evaluation',
            value: (stats.underReviewerAssessment + stats.acceptableForPublication + stats.notAcceptableForPublication).toLocaleString(),
            icon: <CheckCircle className="w-5 h-5 text-green-600" />,
            iconBg: 'bg-green-50',
            subItems: [
                { label: 'Under review', value: stats.underReviewerAssessment.toLocaleString() },
                { label: 'Acceptable for publication', value: stats.acceptableForPublication.toLocaleString() },
                { label: 'Not acceptable for publication', value: stats.notAcceptableForPublication.toLocaleString() },
            ],
        }
        : null;

    const fundingCard: StatCard | null = stats
        ? {
            title: 'Journal Assessment Evaluation',
            value: (stats.acceptableForFunding + stats.notAcceptableForFunding).toLocaleString(),
            icon: <Banknote className="w-5 h-5 text-green-600" />,
            iconBg: 'bg-green-50',
            subItems: [
                { label: 'Acceptable for Funding', value: stats.acceptableForFunding.toLocaleString() },
                { label: 'Not Acceptable for Funding', value: stats.notAcceptableForFunding.toLocaleString() },
            ],
        }
        : null;

    const adminStatsData: StatCard[] = (
        isBookConvener || isThematicUser
            ? [submissionsCard, primaFacieCard, publicationCard]
            : isJournalConvener
                ? [submissionsCard, fundingCard]
                : [submissionsCard, primaFacieCard, publicationCard, fundingCard]
    ).filter((c): c is StatCard => c !== null);

    const accessorStatsData: StatCard[] = [
        { title: 'Total Assigned', value: accessorTotal.toLocaleString(), icon: <FileText className="w-5 h-5 text-gray-500" />, iconBg: 'bg-gray-50' },
        { title: 'Pending Review', value: accessorPending.toLocaleString(), icon: <Clock className="w-5 h-5 text-blue-600" />, iconBg: 'bg-blue-50' },
        { title: 'Completed', value: accessorCompleted.toLocaleString(), icon: <CheckCircle className="w-5 h-5 text-green-600" />, iconBg: 'bg-green-50' },
        { title: 'Returned Manuscripts', value: accessorReturned.toLocaleString(), icon: <Clock className="w-5 h-5 text-orange-600" />, iconBg: 'bg-orange-50' },
    ];

    const activeCards = isAccessor ? accessorStatsData : adminStatsData;

    const cycleBarData = useMemo(() => {
        // Thematic-area users see their own thematic area's totals instead of the per-cycle breakdown.
        if (isThematicUser) {
            const wanted = new Set(thematicCategoryNames.map((n) => n.toLowerCase().trim()));
            return (chartData?.categorySummaries ?? [])
                .filter((c) => wanted.has(c.categoryName.toLowerCase().trim()))
                .map((c) => ({
                    name: c.categoryName,
                    approvedCount: c.approvedCount,
                    rejectedCount: c.rejectedCount,
                    totalSubmissions: c.totalSubmissions,
                }));
        }
        return (chartData?.cycleSummaries ?? []).map((c) => ({
            name: c.cycleName,
            approvedCount: c.approvedCount,
            rejectedCount: c.rejectedCount,
            totalSubmissions: c.totalSubmissions,
        }));
    }, [chartData, isThematicUser, thematicCategoryNames]);

    const categoryColors = useMemo(() => generateDistinctColors(chartData?.categorySummaries?.length ?? 0, 0), [chartData?.categorySummaries?.length]);
    const appTypeColors = useMemo(() => generateDistinctColors(chartData?.applicationTypeSummaries?.length ?? 0, 180), [chartData?.applicationTypeSummaries?.length]);

    const categoryPieData = useMemo(
        () => (chartData?.categorySummaries ?? []).map((c, i) => ({
            name: c.categoryName,
            value: c.totalSubmissions,
            pct: c.percentageOfTotal,
            color: categoryColors[i],
        })),
        [chartData, categoryColors]
    );

    const appTypePieData = useMemo(
        () => (chartData?.applicationTypeSummaries ?? []).map((a, i) => ({
            name: a.applicationType,
            value: a.totalSubmissions,
            pct: a.percentageOfTotal,
            color: appTypeColors[i],
        })),
        [chartData, appTypeColors]
    );

    // Card count varies by role (journal convener sees 2, book convener 3, general admin 4).
    // Cap the columns at the number of cards so a 2-card view doesn't stretch across 4 columns.
    // While loading we render 4 skeletons, so lay them out across 4 columns (horizontal on desktop).
    const statColCount = statsLoading ? 4 : Math.min(activeCards.length || 1, 4);
    const lgColClass = statColCount <= 1 ? 'lg:grid-cols-1'
        : statColCount === 2 ? 'lg:grid-cols-2'
        : statColCount === 3 ? 'lg:grid-cols-3'
        : 'lg:grid-cols-4';
    const statGridCols = `grid grid-cols-1 sm:grid-cols-2 ${lgColClass} gap-4 mb-6`;

    // Reviewer payments are only actionable by DESS (roleId 9 / role 'dess') and the TAG Chairman (roleId 5).
    const isDESSOrTagChairman = user?.roleId === 9 || user?.roleId === 5 || user?.role === 'dess';

    const deskItems = DESK_ITEMS
        .filter((item) => item.key !== 'unPaidReviewerPaymentCount' || isDESSOrTagChairman)
        .map((item) => ({ ...item, count: deskStats?.[item.key] ?? 0 }))
        .filter((item) => item.count > 0);

    const deskContent = (
        <>
            {deskLoading ? (
                <DeskSkeleton />
            ) : deskItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-[200px] gap-3 text-gray-400">
                    <CheckCircle className="w-10 h-10 text-gray-300" />
                    <p className="text-sm">You're all caught up — nothing needs your attention right now.</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {deskItems.map((item) => (
                        <button
                            key={item.key}
                            onClick={() => navigate(item.route)}
                            className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-100 hover:border-blue-200 hover:bg-blue-50/40 transition-colors text-left group"
                        >
                            <div className={`w-10 h-10 rounded-lg ${item.iconBg} flex items-center justify-center flex-shrink-0`}>
                                {item.icon}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">{item.label}</p>
                                <p className="text-xs text-gray-500">{item.count} {item.count === 1 ? 'item' : 'items'} awaiting action</p>
                            </div>
                            <span className="px-2.5 py-1 bg-red-50 text-red-600 text-sm font-semibold rounded-md flex-shrink-0">{item.count}</span>
                            <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-blue-500 transition-colors flex-shrink-0" />
                        </button>
                    ))}
                </div>
            )}
        </>
    );

    const recentReviewsContent = (
        <div className="divide-y divide-gray-50">
            {reviewsLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="py-4 animate-pulse flex items-center gap-4">
                        <div className="h-10 w-10 rounded-lg bg-gray-200 flex-shrink-0" />
                        <div className="flex-1">
                            <div className="h-4 bg-gray-200 rounded w-48 mb-2" />
                            <div className="h-3 bg-gray-200 rounded w-24" />
                        </div>
                        <div className="h-6 bg-gray-200 rounded w-20" />
                    </div>
                ))
            ) : recentReviews.length === 0 ? (
                <div className="py-8 text-center text-gray-400">
                    <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No reviews assigned yet.</p>
                </div>
            ) : (
                recentReviews.map((review) => {
                    return (
                        <button
                            key={review.reviewerAssignmentId}
                            onClick={() => review.conflictOfInterest
                                ? setConflictTarget(review)
                                : navigate(`/admin/review-form/${review.reviewerAssignmentId}`)}
                            className="w-full py-4 hover:bg-gray-50 transition-colors flex items-center gap-4 text-left"
                        >
                            <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center text-gray-500 flex-shrink-0">
                                <FileText className="w-5 h-5" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-0.5">
                                    <h5 className="font-medium text-gray-900 text-sm truncate">{review.title}</h5>
                                    {review.conflictOfInterest && (
                                        <span
                                            title={review.conflictOfInterestReason || 'Conflict of interest declared'}
                                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-red-50 text-red-600 flex-shrink-0"
                                        >
                                            <AlertTriangle className="w-3 h-3" /> Conflict
                                        </span>
                                    )}
                                </div>
                                <p className="text-gray-400 text-xs">ID: {review.submissionNumber} · {review.applicationType}</p>
                            </div>
                            <div className="text-right hidden sm:block flex-shrink-0">
                                <p className="text-xs text-gray-400 mb-1">Assigned</p>
                                <p className="text-sm font-medium text-gray-900">{formatReviewDate(review.assignedAt)}</p>
                            </div>
                            <div className="pl-2 flex-shrink-0">
                                <ReviewStatusBadge status={review.status} />
                            </div>
                        </button>
                    );
                })
            )}
        </div>
    );

    return (
        <div className="w-full">
            <div className="mb-6 flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Dashboard Overview</h1>
                    <p className="text-gray-500 text-sm sm:text-base">
                        Welcome back, {user?.name || 'User'}. Here is the latest operational status.
                    </p>
                </div>
            </div>

            <div className={statGridCols}>
                {statsLoading
                    ? Array.from({ length: 4 }).map((_, i) => <StatCardSkeleton key={i} />)
                    : activeCards.map((stat, i) => (
                        <div key={i} className="bg-white rounded-lg border border-gray-200 p-4">
                            <div className="flex items-start justify-between mb-2">
                                <span className="text-xs text-gray-600 font-medium">{stat.title}</span>
                                <div className={`w-8 h-8 rounded-lg ${stat.iconBg} flex items-center justify-center flex-shrink-0`}>
                                    {stat.icon}
                                </div>
                            </div>
                            <div className="text-2xl font-bold text-gray-900 mb-2">{stat.value}</div>
                            {stat.subItems && stat.subItems.length > 0 && (
                                <div className="border-t border-gray-100 pt-2 flex flex-wrap gap-x-3 gap-y-1">
                                    {stat.subItems.map((sub, j) => (
                                        <div key={j} className="flex items-center gap-1.5">
                                            <span className="text-xs text-gray-400">{sub.label}:</span>
                                            <span className="text-xs font-semibold text-gray-700">{sub.value}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
            </div>

            {/* Charts rows — hidden entirely for accessors */}
            {!isAccessor && (
                <>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                        <div className="bg-white rounded-lg border border-gray-200 p-6">
                            <div className="mb-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-1">Submission & Approval Trends</h3>
                                <p className="text-sm text-gray-500">{isThematicUser ? 'Manuscripts received vs. passed/rejected in your thematic area' : 'Cycle overview of manuscripts received vs. passed/rejected'}</p>
                            </div>

                            {chartsLoading ? (
                                <ChartSkeleton height={300} />
                            ) : cycleBarData.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-[300px] gap-3 text-gray-400">
                                    <BarChart2 className="w-10 h-10 text-gray-300" />
                                    <p className="text-sm">{isThematicUser ? 'No submission data available for your thematic area yet.' : 'No submission data available for any cycle yet.'}</p>
                                </div>
                            ) : (
                                <>
                                    <ResponsiveContainer width="100%" height={300}>
                                        <BarChart data={cycleBarData} barGap={4}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                                            <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#6b7280', fontSize: 12 }} />
                                            <YAxis axisLine={false} tickLine={false} tick={{ fill: '#6b7280', fontSize: 12 }} allowDecimals={false} />
                                            <Tooltip content={<CycleBarTooltip />} />
                                            <Bar dataKey="totalSubmissions" name="Total" fill="#1F2937" radius={[4, 4, 0, 0]} maxBarSize={36} />
                                            <Bar dataKey="approvedCount" name="Approved" fill="#10B981" radius={[4, 4, 0, 0]} maxBarSize={36} />
                                            <Bar dataKey="rejectedCount" name="Rejected" fill="#EF4444" radius={[4, 4, 0, 0]} maxBarSize={36} />
                                        </BarChart>
                                    </ResponsiveContainer>

                                    <div className="flex items-center justify-center gap-6 mt-3">
                                        <div className="flex items-center gap-1.5">
                                            <div className="w-3 h-3 rounded-full bg-gray-800" />
                                            <span className="text-sm text-gray-600">Total</span>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <div className="w-3 h-3 rounded-full bg-green-500" />
                                            <span className="text-sm text-gray-600">Approved</span>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <div className="w-3 h-3 rounded-full bg-red-500" />
                                            <span className="text-sm text-gray-600">Rejected</span>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        {showThematicChart && (
                        <div className="bg-white rounded-lg border border-gray-200 p-6">
                            <div className="mb-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-1">Submissions by Thematic Area</h3>
                                <p className="text-sm text-gray-500">Distribution across thematic areas</p>
                            </div>

                            {chartsLoading ? (
                                <ChartSkeleton height={260} />
                            ) : categoryPieData.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-[260px] gap-3 text-gray-400">
                                    <PieChartIcon className="w-10 h-10 text-gray-300" />
                                    <p className="text-sm">No submissions have been categorized yet.</p>
                                </div>
                            ) : (
                                <>
                                    <ResponsiveContainer width="100%" height={260}>
                                        <PieChart>
                                            <Pie data={categoryPieData} cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={4} dataKey="value">
                                                {categoryPieData.map((entry, i) => <Cell key={`cat-${i}`} fill={entry.color} />)}
                                            </Pie>
                                            <Tooltip content={<PieTooltip />} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                    <PieLegend items={categoryPieData} />
                                </>
                            )}
                        </div>
                        )}
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="bg-white rounded-lg border border-gray-200 p-6">
                            <div className="mb-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-1">Attention Needed</h3>
                                <p className="text-sm text-gray-500">Items on your desk that need action</p>
                            </div>
                            {deskContent}
                        </div>

                        {showAppTypeChart && (
                        <div className="bg-white rounded-lg border border-gray-200 p-6">
                            <div className="mb-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-1">Submissions by Application Type</h3>
                                <p className="text-sm text-gray-500">Breakdown across submission types (e.g. Books, Publications)</p>
                            </div>

                            {chartsLoading ? (
                                <ChartSkeleton height={260} />
                            ) : appTypePieData.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-[260px] gap-3 text-gray-400">
                                    <Inbox className="w-10 h-10 text-gray-300" />
                                    <p className="text-sm">No submissions have been recorded under any application type yet.</p>
                                </div>
                            ) : (
                                <>
                                    <ResponsiveContainer width="100%" height={260}>
                                        <PieChart>
                                            <Pie data={appTypePieData} cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={4} dataKey="value">
                                                {appTypePieData.map((entry, i) => <Cell key={`app-${i}`} fill={entry.color} />)}
                                            </Pie>
                                            <Tooltip content={<PieTooltip />} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                    <PieLegend items={appTypePieData} />
                                </>
                            )}
                        </div>
                        )}
                    </div>
                </>
            )}

            {/* Accessor-only: re-review requests needing attention */}
            {isAccessor && !deskLoading && (deskStats?.pendingReReviewManuscriptRequestCount ?? 0) > 0 && (
                <button
                    onClick={() => navigate('/admin/manuscript-reviews?tab=returned')}
                    className="w-full mb-6 flex items-center gap-3 bg-white rounded-lg border border-gray-200 p-4 hover:border-orange-200 hover:bg-orange-50/40 transition-colors text-left group"
                >
                    <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0">
                        <RefreshCw className="w-5 h-5 text-orange-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">Re-review requests pending</p>
                        <p className="text-xs text-gray-500">
                            {deskStats!.pendingReReviewManuscriptRequestCount} {deskStats!.pendingReReviewManuscriptRequestCount === 1 ? 'manuscript needs' : 'manuscripts need'} your re-review
                        </p>
                    </div>
                    <span className="px-2.5 py-1 bg-red-50 text-red-600 text-sm font-semibold rounded-md flex-shrink-0">{deskStats!.pendingReReviewManuscriptRequestCount}</span>
                    <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-orange-500 transition-colors flex-shrink-0" />
                </button>
            )}

            {/* Accessor-only: recent reviews, no charts */}
            {isAccessor && (
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">Recent Reviews</h3>
                            <p className="text-sm text-gray-500">Your latest review assignments</p>
                        </div>
                        <button
                            onClick={() => navigate('/admin/manuscript-reviews')}
                            className="text-sm font-medium text-gray-900 hover:text-blue-600"
                        >
                            View all
                        </button>
                    </div>
                    <div className="px-6">
                        {recentReviewsContent}
                    </div>
                </div>
            )}

            {/* Conflict of Interest Notes Modal */}
            <Modal
                isOpen={conflictTarget !== null}
                onClose={() => setConflictTarget(null)}
                title="Conflict of Interest"
                description="You declared a conflict of interest for this manuscript, so it can't be reviewed."
                maxWidth="md"
            >
                <div className="space-y-4 mt-2">
                    {conflictTarget && (
                        <div className="bg-gray-50 rounded-lg px-4 py-3 border border-gray-100">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Manuscript</p>
                            <p className="text-sm font-medium text-gray-900 line-clamp-2">{conflictTarget.title}</p>
                            <p className="text-xs text-gray-400 mt-0.5 font-mono">{conflictTarget.submissionNumber}</p>
                        </div>
                    )}

                    <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                        <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                        <div>
                            <p className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-1">Reason</p>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap">
                                {conflictTarget?.conflictOfInterestReason?.trim() || 'No reason provided.'}
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center justify-end pt-2">
                        <button
                            onClick={() => setConflictTarget(null)}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}

export default DashboardOverview;